import { useCallback, useRef, useState } from "react";

const uuidv4 = () => crypto.randomUUID();
import { AGENTCORE_NAMES } from "../../consts";
import streamAIResponse from "../streamAIResponse";
import { CHAT_PHASE } from "./useAIChatPhase";

const TOOLS_USED_RE = /<tools-used\s+value="(\d+)">/;
const TOOLS_USED_STRIP_RE = /<tools-used\s+value="\d+">/g;

/**
 * Extracts and strips `<tools-used value="N">` from raw content.
 * @returns {{ displayContent: string|null, toolsUsed: number|null }}
 */
function parseToolsUsed(rawContent) {
  if (!rawContent) {
    return { displayContent: null, toolsUsed: null };
  }

  const match = rawContent.match(TOOLS_USED_RE);
  const toolsUsed = match ? parseInt(match[1], 10) : null;
  const displayContent = rawContent.replace(TOOLS_USED_STRIP_RE, "").trim();
  return { displayContent, toolsUsed };
}

/**
 * Hook that wraps `streamAIResponse` and drives a `useAIChatPhase` state
 * machine automatically.
 *
 * Owns the AbortController for cancellation and all callback→phase wiring.
 *
 * @param {{
 *   transition: (phase: string, error?: any) => void,
 *   tenantId: string,
 *   apiUrl: string,
 *   workspaceId: string,
 *   pitDate: string,
 *   getToken: () => Promise<string>,
 *   adoptChatId: (id: string) => void,
 *   updateChatOptimistically: (message: object) => void,
 *   refetchChats: () => void,
 *   selectedAgent: string,
 *   onStreamComplete?: (lastMessage: object|null) => void,
 * }} deps
 *
 * @returns {{
 *   send: (prompt: string, opts?: { chatId?: string, isRetry?: boolean }) => Promise<void>,
 *   cancel: () => void,
 *   toolsUsedCount: number,
 * }}
 */
export function useAIChatStream({
  transition,
  tenantId,
  apiUrl,
  workspaceId,
  pitDate,
  getToken,
  adoptChatId,
  updateChatOptimistically,
  refetchChats,
  selectedAgent,
  onStreamComplete,
}) {
  const [toolsUsedCount, setToolsUsedCount] = useState(0);
  const abortRef = useRef(null);
  // Track the chatId the backend returns (may differ from what we sent)
  const responseChatIdRef = useRef(null);
  // Track whether we've seen the first content chunk (for thinking → streaming)
  const hasStreamedRef = useRef(false);
  // Track the last message content for onStreamComplete
  const lastMessageRef = useRef(null);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    transition(CHAT_PHASE.IDLE);
  }, [transition]);

  const send = useCallback(
    async (prompt, { chatId = null, isRetry = false } = {}) => {
      // Reset per-request state
      responseChatIdRef.current = null;
      hasStreamedRef.current = false;
      lastMessageRef.current = null;
      setToolsUsedCount(0);

      // Cancel any in-flight request
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      transition(CHAT_PHASE.CONNECTING);

      // Optimistically show the user message (skip on retry — it already exists)
      if (!isRetry) {
        updateChatOptimistically({
          id: `temp-user-${uuidv4()}`,
          role: "user",
          content: prompt,
        });
      }

      try {
        const token = await getToken();

        await streamAIResponse(
          { prompt, aiChatId: chatId },
          {
            tenantId,
            apiUrl,
            agentName:
              selectedAgent === AGENTCORE_NAMES.GQL_EXPERT_V2
                ? AGENTCORE_NAMES.ASK_TEAMFORM_V2
                : selectedAgent,
            signal: controller.signal,
            headers: {
              Authorization: `Bearer ${token}`,
              "x-workspace-id": workspaceId,
              "x-pit-date": pitDate,
              ...(selectedAgent === AGENTCORE_NAMES.GQL_EXPERT_V2 && {
                "X-Agent-Select": "gql_expert",
              }),
            },

            onConnectionEstablished: (data) => {
              transition(CHAT_PHASE.THINKING);
              if (data?.aiChatId && data.aiChatId !== chatId) {
                responseChatIdRef.current = data.aiChatId;
                adoptChatId(data.aiChatId);
              }
            },

            onError: (e) => {
              transition(CHAT_PHASE.ERROR, e);
            },

            onMessageChunk: (chunkEvent) => {
              if (!chunkEvent?.message) {
                return;
              }

              // Adopt chatId from first chunk if not already adopted
              if (!responseChatIdRef.current && chunkEvent.message.aiChatId) {
                responseChatIdRef.current = chunkEvent.message.aiChatId;
              }

              const rawContent =
                chunkEvent.content || chunkEvent.message.content;
              const { displayContent, toolsUsed } = parseToolsUsed(rawContent);

              if (toolsUsed !== null) {
                setToolsUsedCount(toolsUsed);
              }

              // Skip optimistic update for tool-count-only chunks
              if (!displayContent && toolsUsed !== null) {
                return;
              }

              // First real content chunk → transition to streaming
              if (!hasStreamedRef.current && displayContent) {
                hasStreamedRef.current = true;
                transition(CHAT_PHASE.STREAMING);
              }

              updateChatOptimistically({
                ...chunkEvent.message,
                content: displayContent,
              });
              lastMessageRef.current = {
                ...chunkEvent.message,
                content: displayContent,
              };
            },

            onDone: (finalMessage) => {
              // Adopt chat + refresh list if backend created a new chat
              if (
                responseChatIdRef.current &&
                responseChatIdRef.current !== chatId
              ) {
                adoptChatId(responseChatIdRef.current);
                refetchChats();
              }
              setToolsUsedCount(0);
              transition(CHAT_PHASE.DONE);

              // Use the backend's reconciled final message if available,
              // otherwise fall back to the last chunk we saw.
              const resolved = finalMessage ?? lastMessageRef.current;
              if (resolved) {
                updateChatOptimistically({
                  ...resolved,
                  status: "done",
                });
              }

              // Notify caller (e.g. for dashboard detection)
              onStreamComplete?.(resolved);
            },
          }
        );
      } catch (e) {
        // fetch itself threw (network error, not an SSE error event)
        if (!controller.signal.aborted) {
          transition(CHAT_PHASE.ERROR, e);
        }
      }
    },
    [
      transition,
      tenantId,
      apiUrl,
      workspaceId,
      pitDate,
      getToken,
      adoptChatId,
      updateChatOptimistically,
      refetchChats,
      selectedAgent,
      onStreamComplete,
    ]
  );

  return { send, cancel, toolsUsedCount };
}
